import { useState } from 'react';
import { createApplication } from '../api/client.js';
import { useAppStore } from '../store/useAppStore.js';
import { Status } from '@launchpad/shared';

const STATUS_MAP = {
  'applied': Status.APPLIED,
  'interviewing': Status.INTERVIEWING,
  'offer': Status.OFFER,
  'rejected': Status.REJECTED,
  'saved': Status.SAVED,
};

function mapStatus(raw) {
  if (!raw) return Status.SAVED;
  const lower = raw.trim().toLowerCase();
  return STATUS_MAP[lower] || Status.SAVED;
}

function parseDate(raw) {
  if (!raw) return '';
  // Handle formats like "11/19/2025", "3/13/26", "4/2/2026"
  const parts = raw.trim().split('/');
  if (parts.length !== 3) return '';
  let [month, day, year] = parts;
  if (year.length === 2) year = '20' + year;
  return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
}

function parseTSV(text) {
  const rows = [];
  let current = '';
  let inQuotes = false;
  const lines = [];

  // Split into logical lines respecting quoted fields
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
      current += ch;
    } else if (ch === '\n' && !inQuotes) {
      lines.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  if (current.trim()) lines.push(current);

  if (lines.length < 2) return [];

  const headers = lines[0].split('\t');

  for (let i = 1; i < lines.length; i++) {
    const values = splitTSVLine(lines[i]);
    const row = {};
    headers.forEach((h, idx) => {
      let val = (values[idx] || '').trim();
      // Strip surrounding quotes
      if (val.startsWith('"') && val.endsWith('"')) {
        val = val.slice(1, -1).replace(/""/g, '"');
      }
      row[h.trim()] = val;
    });
    if (row['Company']) rows.push(row);
  }

  return rows;
}

function splitTSVLine(line) {
  const fields = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === '\t' && !inQuotes) {
      fields.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  fields.push(current);
  return fields;
}

function mapRow(row) {
  return {
    company: row['Company'] || '',
    role: row['Role Title'] || '',
    status: mapStatus(row['Status']),
    dateApplied: parseDate(row['Date Applied']),
    jobUrl: '',
    jobDescription: (row['JD'] || '').replace(/^"|"$/g, ''),
    notes: [
      row['Notes'],
      row['Why Match?'] ? `## Why Match?\n${row['Why Match?']}` : '',
      row['Warm Contact'] ? `## Warm Contact\n${row['Warm Contact']}` : '',
    ].filter(Boolean).join('\n\n'),
    location: row['Location'] || '',
    source: row['Source'] || '',
    nextAction: row['Next Action'] || '',
    nextActionDate: parseDate(row['Next Action Date']),
    contactName: row['Hiring Mgr/Team'] || '',
    tags: [
      row['Focus Model'] ? row['Focus Model'].split('(')[0].trim() : '',
    ].filter(Boolean),
  };
}

export function Import() {
  const addApplicationLocal = useAppStore((s) => s.addApplicationLocal);
  const [input, setInput] = useState('');
  const [preview, setPreview] = useState([]);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);

  function handlePreview() {
    const rows = parseTSV(input);
    const mapped = rows.filter((r) => r['Company']).map(mapRow);
    setPreview(mapped);
    setResults(null);
  }

  async function handleImport() {
    setImporting(true);
    let success = 0;
    let failed = 0;

    for (const app of preview) {
      try {
        const created = await createApplication(app);
        addApplicationLocal(created);
        success++;
      } catch {
        failed++;
      }
    }

    setResults({ success, failed, total: preview.length });
    setImporting(false);
    setPreview([]);
    setInput('');
  }

  return (
    <div className="p-6 max-w-4xl space-y-6">
      <h1 className="text-lg font-semibold text-slate-900">Import Applications</h1>
      <p className="text-sm text-slate-600">
        Paste your spreadsheet data below (tab-separated). Copy all rows including the header row from your spreadsheet.
      </p>

      <textarea
        value={input}
        onChange={(e) => setInput(e.target.value)}
        placeholder="Paste spreadsheet data here (Ctrl+V / Cmd+V)..."
        rows={8}
        className="w-full rounded border border-slate-300 px-3 py-2 text-base font-mono focus:outline-none focus:ring-2 focus:ring-primary-500 resize-y"
      />

      <div className="flex gap-3">
        <button
          onClick={handlePreview}
          disabled={!input.trim()}
          className="rounded bg-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-300 disabled:opacity-50"
        >
          Preview
        </button>
        {preview.length > 0 && (
          <button
            onClick={handleImport}
            disabled={importing}
            className="rounded bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50"
          >
            {importing ? `Importing (${preview.length})...` : `Import ${preview.length} Applications`}
          </button>
        )}
      </div>

      {results && (
        <div className={`rounded p-3 text-sm ${results.failed ? 'bg-amber-50 text-amber-800' : 'bg-green-50 text-green-800'}`}>
          Imported {results.success} of {results.total} applications.
          {results.failed > 0 && ` ${results.failed} failed.`}
        </div>
      )}

      {preview.length > 0 && (
        <div className="space-y-2">
          <h2 className="text-sm font-medium text-slate-700">Preview ({preview.length} applications)</h2>
          <div className="overflow-x-auto rounded border border-slate-200">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 text-left">
                  <th className="px-3 py-2">Company</th>
                  <th className="px-3 py-2">Role</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Date Applied</th>
                  <th className="px-3 py-2">Location</th>
                  <th className="px-3 py-2">Source</th>
                </tr>
              </thead>
              <tbody>
                {preview.map((app, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-3 py-1.5 font-medium">{app.company}</td>
                    <td className="px-3 py-1.5">{app.role}</td>
                    <td className="px-3 py-1.5">{app.status}</td>
                    <td className="px-3 py-1.5">{app.dateApplied}</td>
                    <td className="px-3 py-1.5">{app.location}</td>
                    <td className="px-3 py-1.5">{app.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
